import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform, ScrollView, ActivityIndicator, Alert } from 'react-native';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp, writeBatch, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUser } from './LoginPersistence';
import { getResponsiveValue } from '../styles/breakpoints';
import { Clipboard } from 'react-native';

const INVITE_CODES_PER_MONTH = 10;

// Generate a unique invite code
const generateInviteCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excluding confusing characters
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Get the current month key (YYYY-MM format)
const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Get the first day of current month
const getFirstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

// Get the last day of current month
const getLastDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
};

export const InviteCodes = () => {
  const { user } = useUser();
  const db = getFirestore();
  const functions = getFunctions();

  const [inviteCodes, setInviteCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState('');
  const [sendingEmails, setSendingEmails] = useState(false);
  const [availableCodes, setAvailableCodes] = useState(0);
  const [monthKey, setMonthKey] = useState(getCurrentMonthKey());

  // Check if user is a community member
  useEffect(() => {
    if (!user?.isCommunityMember) {
      setLoading(false);
    }
  }, [user]);

  // Load invite codes for the current month
  useEffect(() => {
    if (user?.uid && user?.isCommunityMember) {
      loadInviteCodes();
    }
  }, [user?.uid, user?.isCommunityMember, monthKey]);

  // Check if codes are expired
  const areCodesExpired = (codes) => {
    if (codes.length === 0) return true;
    const now = new Date();
    return codes.every(code => {
      const expiresAt = code.expiresAt?.toDate?.() || new Date(0);
      return now > expiresAt;
    });
  };

  // Generate all invite codes for the month (internal function)
  const generateInviteCodesInternal = async (currentMonthKey) => {
    if (!user?.uid) return false;

    try {
      const expiresAt = getLastDayOfMonth();

      // Check existing codes for current month
      const existingCodesQuery = query(
        collection(db, 'invite_codes'),
        where('createdBy', '==', user.uid),
        where('monthKey', '==', currentMonthKey)
      );
      const existingSnapshot = await getDocs(existingCodesQuery);
      const existingCodes = [];
      existingSnapshot.forEach((docSnap) => {
        existingCodes.push({ id: docSnap.id, ...docSnap.data() });
      });

      // Check if we need to generate codes
      const hasExpiredCodes = areCodesExpired(existingCodes);
      const hasUnusedCodes = existingCodes.some(code => {
        if (code.isUsed) return false;
        const expiresAt = code.expiresAt?.toDate?.() || new Date(0);
        return new Date() <= expiresAt;
      });

      // If codes exist and are not expired, don't generate new ones
      if (existingCodes.length > 0 && !hasExpiredCodes && hasUnusedCodes) {
        return false; // Codes already exist and are valid
      }

      // If codes are expired, we'll generate new ones (but keep old ones for history)
      // Calculate how many codes to generate
      const codesToGenerate = hasExpiredCodes ? INVITE_CODES_PER_MONTH : Math.max(0, INVITE_CODES_PER_MONTH - existingCodes.length);

      if (codesToGenerate === 0) {
        return false; // Already have max codes
      }

      // Generate codes in batch
      const batch = writeBatch(db);
      const generatedCodes = [];
      const allCodes = new Set(); // Track all codes to ensure uniqueness

      // Add existing codes to the set
      existingCodes.forEach(code => allCodes.add(code.code));

      let attempts = 0;
      const maxAttempts = codesToGenerate * 20; // Allow more attempts for batch generation

      while (generatedCodes.length < codesToGenerate && attempts < maxAttempts) {
        let code = generateInviteCode();
        attempts++;

        // Check if code already exists in our batch or in database
        if (allCodes.has(code)) {
          continue;
        }

        // Double-check against database
        const checkQuery = query(
          collection(db, 'invite_codes'),
          where('code', '==', code)
        );
        const checkSnapshot = await getDocs(checkQuery);
        
        if (!checkSnapshot.empty) {
          allCodes.add(code);
          continue;
        }

        // Code is unique, add it
        allCodes.add(code);
        generatedCodes.push(code);

        // Add to batch
        const newCodeRef = doc(collection(db, 'invite_codes'));
        batch.set(newCodeRef, {
          code,
          createdBy: user.uid,
          createdAt: serverTimestamp(),
          expiresAt: expiresAt,
          monthKey: currentMonthKey,
          isUsed: false,
          usedBy: null,
          usedAt: null
        });
      }

      if (generatedCodes.length === 0) {
        console.error('Failed to generate unique codes');
        return false;
      }

      // Commit batch
      await batch.commit();
      return true; // Successfully generated codes
    } catch (error) {
      console.error('Error generating invite codes:', error);
      return false;
    }
  };

  const loadInviteCodes = async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);
      const currentMonthKey = getCurrentMonthKey();
      setMonthKey(currentMonthKey);

      // Load invite codes for current month
      const inviteCodesQuery = query(
        collection(db, 'invite_codes'),
        where('createdBy', '==', user.uid),
        where('monthKey', '==', currentMonthKey)
      );

      const snapshot = await getDocs(inviteCodesQuery);
      const codes = [];
      snapshot.forEach((docSnap) => {
        codes.push({ id: docSnap.id, ...docSnap.data() });
      });

      // Sort by creation date (newest first)
      codes.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      });

      // Check if we need to generate codes automatically
      const needsGeneration = codes.length === 0 || areCodesExpired(codes);
      
      if (needsGeneration) {
        // Automatically generate codes
        const generated = await generateInviteCodesInternal(currentMonthKey);
        
        if (generated) {
          // Reload codes after generation
          const newSnapshot = await getDocs(inviteCodesQuery);
          const newCodes = [];
          newSnapshot.forEach((docSnap) => {
            newCodes.push({ id: docSnap.id, ...docSnap.data() });
          });
          
          newCodes.sort((a, b) => {
            const aTime = a.createdAt?.toDate?.() || new Date(0);
            const bTime = b.createdAt?.toDate?.() || new Date(0);
            return bTime - aTime;
          });
          
          setInviteCodes(newCodes);
          const unusedNewCodes = newCodes.filter(code => !code.isUsed);
          setAvailableCodes(unusedNewCodes.length);
        } else {
          // Use existing codes even if expired (for display)
          setInviteCodes(codes);
          const unusedCodes = codes.filter(code => !code.isUsed);
          setAvailableCodes(unusedCodes.length);
        }
      } else {
        // Codes exist and are valid
        setInviteCodes(codes);
        const unusedCodes = codes.filter(code => !code.isUsed);
        setAvailableCodes(unusedCodes.length);
      }

    } catch (error) {
      console.error('Error loading invite codes:', error);
      Alert.alert('Error', 'Failed to load invite codes');
    } finally {
      setLoading(false);
    }
  };


  const copyToClipboard = async (code) => {
    try {
      if (Platform.OS === 'web') {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = code;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
      } else {
        Clipboard.setString(code);
      }
      Alert.alert('Copied!', `Invite code "${code}" copied to clipboard`);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      Alert.alert('Error', 'Failed to copy to clipboard');
    }
  };

  const sendInviteCodeToEmail = async () => {
    if (!emailInput.trim()) {
      Alert.alert('Error', 'Please enter at least one email address');
      return;
    }

    // Parse CSV emails
    const emailList = emailInput
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (emailList.length === 0) {
      Alert.alert('Error', 'Please enter at least one valid email address');
      return;
    }

    // Validate all emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emailList.filter(email => !emailRegex.test(email));
    
    if (invalidEmails.length > 0) {
      Alert.alert('Invalid Emails', `The following emails are invalid:\n${invalidEmails.join('\n')}`);
    return;
    }

    // Get all unused codes
    const unusedCodes = inviteCodes.filter(code => !code.isUsed);
    
    if (unusedCodes.length === 0) {
      Alert.alert('No Codes', 'You have no unused invite codes.');
      return;
    }

    const baseUrl = Platform.OS === 'web' ? (window.location?.origin || 'https://venuereportcard.com') : 'https://venuereportcard.com';

    try {
      setSendingEmails(true);

      // Check which emails already exist in the database
      const existingEmails = [];
      const newEmails = [];

      for (const email of emailList) {
        try {
          const usersRef = collection(db, 'users');
          const q = query(
            usersRef,
            where('email', '==', email.toLowerCase()),
            limit(1)
          );
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            existingEmails.push(email);
          } else {
            newEmails.push(email);
          }
        } catch (error) {
          console.error(`Error checking email ${email}:`, error);
          // If we can't check, assume it's new and try to send
          newEmails.push(email);
        }
      }

      // If all emails are already members, show alert and return
      if (newEmails.length === 0) {
        setSendingEmails(false);
        return;
      }

      const handleEmail = httpsCallable(functions, 'handleEmail');
      const expiresAt = unusedCodes[0]?.expiresAt?.toDate?.() || getLastDayOfMonth();
      
      // Prepare all invite codes data
      const inviteCodesData = unusedCodes.map(code => ({
        code: code.code,
        inviteUrl: `${baseUrl}/community?inviteCode=${code.code}`,
        expiresAt: (code.expiresAt?.toDate?.() || getLastDayOfMonth()).toLocaleDateString()
      }));

      // Send emails to each new recipient
      const results = {
        success: [],
        failed: [],
        skipped: existingEmails
      };

      for (const email of newEmails) {
        try {
          await handleEmail({
            template: 'invite_code_multiple',
            recipient: email,
            emailData: {
              inviteCodes: inviteCodesData,
              senderName: user?.username || user?.firstName || 'A Community Member',
              senderEmail: user?.email,
              expiresAt: expiresAt.toLocaleDateString(),
              totalCodes: unusedCodes.length
            }
          });
          results.success.push(email);
        } catch (error) {
          console.error(`Error sending email to ${email}:`, error);
          results.failed.push({ email, error: error.message || 'Unknown error' });
        }
      }

      // Show results
      let message = '';
      if (results.success.length > 0) {
        message += `${unusedCodes.length} invite code${unusedCodes.length > 1 ? 's' : ''} sent to ${results.success.length} recipient${results.success.length > 1 ? 's' : ''}:\n${results.success.join('\n')}`;
      }
      
      if (results.skipped.length > 0) {
        if (message) message += '\n\n';
        message += `Skipped ${results.skipped.length} already member${results.skipped.length > 1 ? 's' : ''}:\n${results.skipped.join('\n')}`;
      }
      
      if (results.failed.length > 0) {
        if (message) message += '\n\n';
        message += `Failed for ${results.failed.length} recipient${results.failed.length > 1 ? 's' : ''}:\n${results.failed.map(f => `${f.email}: ${f.error}`).join('\n')}`;
      }

      if (results.success.length > 0 && results.failed.length === 0) {
        Alert.alert('Success', message);
        setEmailInput('');
      } else if (results.success.length > 0 && results.failed.length > 0) {
        Alert.alert('Partial Success', message);
      } else if (results.failed.length > 0) {
        Alert.alert('Error', message);
      }

    } catch (error) {
      console.error('Error sending invite codes:', error);
      Alert.alert('Error', 'Failed to send invite codes');
    } finally {
      setSendingEmails(false);
    }
  };

  if (!user?.isCommunityMember) {
    return (
      <View style={styles.container}>
        <View style={styles.adminContent}>
          <Text style={styles.noAccessText}>
            You need to be a community member to access invite codes.
          </Text>
        </View>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.adminContent}>
          <ActivityIndicator size="large" color="#4e7de0" />
          <Text style={styles.loadingText}>Loading invite codes...</Text>
        </View>
      </View>
    );
  }

  // Invite Code Item Component
  const InviteCodeItem = ({ inviteCode, onCopy }) => {
    const expiresAt = inviteCode.expiresAt?.toDate?.() || getLastDayOfMonth();
    const isExpired = new Date() > expiresAt;
    const isUsed = inviteCode.isUsed;
    const usedAt = inviteCode.usedAt?.toDate?.();

    if (isUsed) {
      return (
        <View style={[styles.codeItem, styles.usedCode]}>
          <View style={styles.codeContent}>
            <Text style={[styles.codeText, styles.usedCodeText]}>{inviteCode.code}</Text>
            <Text style={styles.codeMeta}>
              Used on: {usedAt ? usedAt.toLocaleDateString() : 'Unknown'}
            </Text>
          </View>
          <Text style={styles.usedBadge}>USED</Text>
        </View>
      );
    }

    return (
      <TouchableOpacity
        style={[
          styles.codeItem,
          isExpired && styles.expiredCode
        ]}
        onPress={() => onCopy(inviteCode.code)}
      >
        <View style={styles.codeContent}>
          <Text style={styles.codeText}>{inviteCode.code}</Text>
          <Text style={styles.codeMeta}>
            {isExpired 
              ? 'Expired' 
              : `Expires: ${expiresAt.toLocaleDateString()}`
            }
          </Text>
        </View>
        <Text style={styles.copyHint}>Tap to copy</Text>
      </TouchableOpacity>
    );
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <View style={styles.adminContent}>
        {/* All Invite Codes Section */}
        {inviteCodes.length > 0 && (
          <View style={styles.codesSection}>
            <Text style={styles.sectionTitle}>
              Invite Codes ({inviteCodes.length})
            </Text>
            <Text style={styles.sectionSubtext}>
              Click unused codes to copy them to clipboard
            </Text>
            {inviteCodes.map((inviteCode) => (
              <InviteCodeItem
                key={inviteCode.id}
                inviteCode={inviteCode}
                onCopy={copyToClipboard}
              />
            ))}
          </View>
        )}

                {/* Email Input Section */}
                <View style={styles.emailSection}>
          <Text style={styles.sectionTitle}>Send Invite Codes via Email</Text>
          <Text style={styles.sectionSubtext}>
            All available unused codes ({inviteCodes.filter(code => !code.isUsed).length}) will be sent to each email address. Separate multiple emails with commas.
          </Text>
          <TextInput
            style={styles.emailInput}
            placeholder="Enter email address(es), e.g., email1@example.com, email2@example.com"
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
            value={emailInput}
            onChangeText={setEmailInput}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            multiline
          />

          <TouchableOpacity
            style={[
              styles.sendButton,
              (!emailInput.trim() || sendingEmails || inviteCodes.filter(code => !code.isUsed).length === 0) && styles.disabledButton
            ]}
            onPress={sendInviteCodeToEmail}
            disabled={!emailInput.trim() || sendingEmails || inviteCodes.filter(code => !code.isUsed).length === 0}
          >
            {sendingEmails ? (
              <>
                <ActivityIndicator size="small" color="#fff" style={{ marginRight: 8 }} />
                <Text style={styles.sendButtonText}>Sending...</Text>
              </>
            ) : (
              <Text style={styles.sendButtonText}>
                Send All Codes ({inviteCodes.filter(code => !code.isUsed).length}) to {emailInput.split(',').filter(e => e.trim()).length || 0} Email{emailInput.split(',').filter(e => e.trim()).length !== 1 ? 's' : ''}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000000',
  },
  scrollContent: {
    flexGrow: 1,
  },
  adminContent: {
    flex: 1,
    padding: 20,
  },
  statsContainer: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
    marginBottom: 20,
  },
  statsText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: 5,
  },
  statsSubtext: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
  },
  actionContainer: {
    marginBottom: 30,
  },
  generateButton: {
    backgroundColor: '#4e7de0',
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  generateButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  disabledButton: {
    backgroundColor: '#434252',
    opacity: 0.6,
  },
  emailSection: {
    marginBottom: 30,
    padding: 15,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
  },
  sectionTitle: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: 10,
  },
  sectionSubtext: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    marginBottom: 15,
  },
  emailInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 8,
    padding: 15,
    color: '#ffffff',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    marginBottom: 15,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  sendButton: {
    backgroundColor: '#2ecc71',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  sendButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  codesSection: {
    marginBottom: 30,
  },
  codeItem: {
    backgroundColor: '#4e7de0',
    borderRadius: 8,
    padding: 15,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderLeftWidth: 3,
    borderLeftColor: '#4e7de0',
  },
  expiredCode: {
    borderLeftColor: '#e74c3c',
    opacity: 0.6,
  },
  usedCode: {
    borderLeftColor: '#95a5a6',
    opacity: 0.7,
  },
  codeContent: {
    flex: 1,
  },
  codeText: {
    color: '#97ff11',
    fontSize: 24,
    fontFamily: 'PlayPen Sans',
    fontWeight: 'bold',
    marginBottom: 5,
    letterSpacing: 2,
  },
  usedCodeText: {
    textDecorationLine: 'line-through',
    opacity: 0.5,
  },
  codeMeta: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 10,
    fontFamily: 'VastagoGrotesk',
  },
  copyHint: {
    color: '#FFFFFF',
    fontSize: 10,
    fontFamily: 'Glacial Indifference',
    fontStyle: 'italic',
  },
  usedBadge: {
    color: '#95a5a6',
    fontSize: 8,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
    backgroundColor: 'rgba(149, 165, 166, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  emptyStateText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    marginTop: 10,
    textAlign: 'center',
  },
  noAccessText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
    marginTop: 40,
  },
});

export default InviteCodes;

